import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Camera, Loader2, ShoppingBag, ExternalLink, X, Package, TrendingUp, Info, Star, Shield, Truck, ChevronLeft, ChevronRight, Tag, Factory, Store, MapPin, ThumbsUp, Box, Minus, Plus, Wallet, Landmark, ArrowLeft, CheckCircle2, ShoppingCart } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from './ui/dialog';
import { sourcingAPI, exchangeRateAPI, walletAPI, procurementAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import MembershipGate from './MembershipGate';
import { PINSetupDialog, PINVerificationDialog } from './WalletPIN';

// Chinese-to-English label translation map for common variant/attribute names
const ZH_TO_EN = {
  '颜色': 'Color', '颜色分类': 'Color', '尺码': 'Size', '尺寸': 'Size',
  '规格': 'Specification', '适用型号': 'Compatible Model', '适用手机型号': 'Phone Model',
  '材质': 'Material', '款式': 'Style', '容量': 'Capacity', '长度': 'Length',
  '重量': 'Weight', '功率': 'Power', '品牌': 'Brand', '产地': 'Origin',
  '包装': 'Packaging', '型号': 'Model', '适用品牌': 'Compatible Brand',
};
function translateLabel(label) {
  if (!label) return label;
  return ZH_TO_EN[label] || label;
}

// Product result card
function SourcingProductCard({ product, onSelect, exchangeRate }) {
  const ngnPrice = exchangeRate ? (product.marked_price_rmb * exchangeRate).toLocaleString(undefined, { maximumFractionDigits: 0 }) : null;
  return (
    <div onClick={() => onSelect(product)} className="group bg-white rounded-xl border border-slate-100 hover:border-primary/30 hover:shadow-lg transition-all cursor-pointer overflow-hidden" data-testid={`sourcing-product-${product.id}`}>
      <div className="aspect-square bg-slate-50 relative overflow-hidden">
        {product.image ? (
          <img src={product.image} alt={product.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform" loading="lazy" referrerPolicy="no-referrer" onError={(e) => { e.target.style.display='none'; e.target.nextSibling && (e.target.nextSibling.style.display='flex'); }} />
        ) : null}
        <div className={`w-full h-full items-center justify-center ${product.image ? 'hidden' : 'flex'}`}><Package className="h-10 w-10 text-slate-300" /></div>
        <div className="absolute top-2 left-2 bg-red-500 text-white text-[9px] px-2 py-0.5 rounded-md font-bold">{product.source}</div>
        {product.sales > 0 && (
          <div className="absolute bottom-2 right-2 bg-black/60 text-white text-[9px] px-1.5 py-0.5 rounded flex items-center gap-0.5">
            <TrendingUp className="h-2.5 w-2.5" />{product.sales > 1000 ? `${(product.sales/1000).toFixed(1)}k` : product.sales} sold
          </div>
        )}
      </div>
      <div className="p-3">
        <p className="text-xs text-slate-700 line-clamp-2 min-h-[32px] group-hover:text-primary transition-colors font-medium">{product.title || 'Untitled Product'}</p>
        <div className="mt-2 flex items-baseline gap-2">
          <p className="text-base font-bold text-primary">¥{product.marked_price_rmb?.toFixed(2)}</p>
          {product.base_price_rmb !== product.marked_price_rmb && (
            <p className="text-[10px] text-slate-400 line-through">¥{product.base_price_rmb?.toFixed(2)}</p>
          )}
        </div>
        {ngnPrice && <p className="text-[10px] text-slate-500 mt-0.5">≈ ₦{ngnPrice}</p>}
      </div>
    </div>
  );
}

// ─── RICH PRODUCT DETAIL MODAL (1688-style experience) ──────────────────────
function SourcingCheckoutModal({ product: initialProduct, open, onClose, exchangeRate, markup, minFee = 2, isAuthenticated, isMember, onMembershipRequired }) {
  const navigate = useNavigate();
  const [product, setProduct] = useState(initialProduct);
  const [loading, setLoading] = useState(false);
  const [quantity, setQuantity] = useState(1);
  const [selectedVariants, setSelectedVariants] = useState({});
  const [notes, setNotes] = useState('');
  const [activeImage, setActiveImage] = useState(0);
  const [activeTab, setActiveTab] = useState('detail');
  // Direct checkout state
  const [step, setStep] = useState('product'); // product | payment
  const [wallet, setWallet] = useState(null);
  const [paying, setPaying] = useState(false);
  const [showPINSetup, setShowPINSetup] = useState(false);
  const [showPINVerify, setShowPINVerify] = useState(false);
  const [payCurrency, setPayCurrency] = useState('rmb');
  const [lines, setLines] = useState([]);
  const [manualPick, setManualPick] = useState(false);
  const [freight, setFreight] = useState(null);
  const submissionIdRef = useRef(null);
  const createdItemRef = useRef(null);

  // Load full detail when product changes
  useEffect(() => {
    if (initialProduct) {
      setProduct(initialProduct);
      setQuantity(initialProduct.moq > 1 ? initialProduct.moq : 1);
      setSelectedVariants({});
      setLines([]);
      setManualPick(false);
      setFreight(initialProduct.domestic_freight || null);
      setNotes('');
      setActiveImage(0);
      setActiveTab('detail');
      setStep('product');
      setPaying(false);
      submissionIdRef.current = (window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`);
      createdItemRef.current = null;
      // If product came from search (no skus), fetch full detail
      if (!initialProduct.skus || initialProduct.skus.length === 0) {
        fetchFullDetail(initialProduct);
      }
    }
  }, [initialProduct?.id]);

  const fetchFullDetail = async (p) => {
    setLoading(true);
    try {
      const res = await sourcingAPI.getProductDetail(p.id, p.source, p.source_url);
      if (res.data?.product) {
        setProduct(prev => ({ ...prev, ...res.data.product }));
        if (res.data.product.moq > 1) setQuantity(res.data.product.moq);
      }
    } catch { /* keep existing data */ }
    finally { setLoading(false); }
  };

  // Live supplier domestic freight — debounced recalc on qty/SKU changes
  useEffect(() => {
    if (!product?.id || !product?.source) return;
    const totalQ = lines.length > 0 ? lines.reduce((s, l) => s + l.qty, 0) : quantity;
    const t = setTimeout(async () => {
      try {
        const r = await sourcingAPI.freightEstimate({ platform: product.source, item_id: product.id, quantity: totalQ });
        setFreight(r.data);
      } catch {}
    }, 500);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product?.id, quantity, lines, selectedVariants]);

  if (!product) return null;

  // SKU matching
  const matchedSku = product.skus?.find(s => {
    const opts = s.options || {};
    return Object.keys(selectedVariants).length > 0 && Object.entries(selectedVariants).every(([k, v]) => opts[k] === v);
  });
  const unitPrice = matchedSku ? (matchedSku.marked_price || product.marked_price_rmb) : product.marked_price_rmb;
  const baseUnit = matchedSku ? (matchedSku.price || product.base_price_rmb) : product.base_price_rmb;
  const effectiveLines = lines.length > 0 ? lines : [{ id: 'current', variantStr: '', baseUnit: baseUnit || 0, qty: quantity, image: null }];
  const orderBaseTotal = effectiveLines.reduce((s, l) => s + (l.baseUnit || 0) * l.qty, 0);
  const orderFeeTotal = Math.max(Math.round(orderBaseTotal * ((markup || 0) / 100) * 100) / 100, Number(minFee) || 0);
  const freightFee = (freight && (freight.status === 'free' || freight.status === 'flat') && freight.fee_rmb != null) ? freight.fee_rmb : 0;
  const totalPrice = Math.round((orderBaseTotal + orderFeeTotal + freightFee) * 100) / 100;
  const ngnUnit = exchangeRate ? Math.round(unitPrice * exchangeRate) : null;
  const ngnTotal = exchangeRate ? Math.round(totalPrice * exchangeRate) : null;
  const skuImage = matchedSku?.img_url;
  const images = product.images?.length ? product.images : (product.image ? [product.image] : []);
  const currentImg = (!manualPick && skuImage) ? skuImage : (images[activeImage] || skuImage || images[0]);
  const skuStock = matchedSku ? matchedSku.quantity : product.quantity;

  const handleOrder = () => {
    // Check membership for authenticated users
    if (isAuthenticated && !isMember) {
      onClose();
      onMembershipRequired?.();
      return;
    }
    const variantStr = Object.entries(selectedVariants).map(([k, v]) => `${k}: ${v}`).join(', ');
    const noteText = [variantStr, notes].filter(Boolean).join('. ');
    const params = new URLSearchParams({
      prefill: 'sourcing', product_name: product.title || '', product_link: product.source_url || '',
      price_rmb: String(unitPrice || ''), quantity: String(quantity),
      image: currentImg || '', notes: noteText
    });
    if (isAuthenticated) {
      navigate(`/procurement?${params.toString()}`);
      toast.success('Item added to procurement — complete the form to place your order.');
    } else {
      toast.info('Create an account to place this order');
      navigate(`/register?redirect=/procurement?${params.toString()}`);
    }
    onClose();
  };

  // ── Direct checkout helpers ──
  const variantGroups = product.variant_groups ? Object.keys(product.variant_groups) : [];
  const allVariantsSelected = variantGroups.every(g => selectedVariants[g]);
  const trs = product.translations || {};
  const trv = (s) => trs[s] || s;
  const variantStr = Object.entries(selectedVariants).map(([k, v]) => `${trv(k) !== k ? trv(k) : translateLabel(k)}: ${trv(v)}`).join(', ');
  const baseTotal = orderBaseTotal;
  const feeTotal = orderFeeTotal;
  const totalQty = effectiveLines.reduce((s, l) => s + l.qty, 0);

  const addLine = () => {
    if (variantGroups.length > 0 && !allVariantsSelected) {
      toast.error(`Please select: ${variantGroups.filter(g => !selectedVariants[g]).map(translateLabel).join(', ')}`);
      return;
    }
    const key = variantStr || 'default';
    setLines(prev => {
      const i = prev.findIndex(l => l.key === key);
      if (i >= 0) {
        const cp = [...prev];
        cp[i] = { ...cp[i], qty: cp[i].qty + quantity };
        return cp;
      }
      return [...prev, { id: `${Date.now()}-${prev.length}`, key, variantStr, baseUnit: baseUnit || 0, qty: quantity, image: currentImg || '' }];
    });
    toast.success('Selection added — pick another option or checkout');
  };

  const updateLineQty = (id, delta) => {
    setLines(prev => prev.map(l => l.id === id ? { ...l, qty: Math.max(1, l.qty + delta) } : l).filter(l => l.qty > 0));
  };

  const removeLine = (id) => setLines(prev => prev.filter(l => l.id !== id));

  const gateChecks = () => {
    if (!isAuthenticated) {
      toast.info('Create an account to place this order');
      const params = new URLSearchParams({
        prefill: 'sourcing', product_name: product.title || '', product_link: product.source_url || '',
        price_rmb: String(unitPrice || ''), quantity: String(quantity), image: currentImg || '',
        notes: [variantStr, notes].filter(Boolean).join('. ')
      });
      navigate(`/register?redirect=/procurement?${params.toString()}`);
      onClose();
      return false;
    }
    if (!isMember) { onClose(); onMembershipRequired?.(); return false; }
    if (lines.length === 0 && variantGroups.length > 0 && !allVariantsSelected) {
      toast.error(`Please select: ${variantGroups.filter(g => !selectedVariants[g]).map(translateLabel).join(', ')}`);
      return false;
    }
    return true;
  };

  const ensureOrder = async () => {
    if (createdItemRef.current) return createdItemRef.current;
    const res = await sourcingAPI.directOrder({
      product_name: product.title || 'Marketplace product',
      product_link: product.source_url || '',
      platform: product.source || '',
      item_id: product.id || '',
      base_price_rmb: baseUnit,
      quantity,
      variant: variantStr,
      notes,
      image: currentImg || '',
      submission_id: submissionIdRef.current,
      lines: effectiveLines.map(l => ({
        base_price_rmb: l.baseUnit,
        quantity: l.qty,
        variant: l.id === 'current' ? variantStr : l.variantStr,
        image: l.image || currentImg || '',
      })),
    });
    createdItemRef.current = res.data.item_ids || [res.data.item_id || res.data._id];
    return createdItemRef.current;
  };

  const startCheckout = async () => {
    if (!gateChecks()) return;
    try {
      const w = await walletAPI.getWallet();
      setWallet(w.data);
    } catch { setWallet(null); }
    setStep('payment');
  };

  const addToOrders = async () => {
    if (!gateChecks()) return;
    setPaying(true);
    try {
      await ensureOrder();
      toast.success('Added to your orders — quoted and ready to pay anytime from the Orders page.');
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not add to orders');
    } finally { setPaying(false); }
  };

  const payWithWallet = (currency) => {
    setPayCurrency(currency);
    if (!wallet?.has_pin) { setShowPINSetup(true); return; }
    setShowPINVerify(true);
  };

  const completeWalletPayment = async (pinToken) => {
    setShowPINVerify(false);
    setPaying(true);
    try {
      const itemIds = await ensureOrder();
      await procurementAPI.checkout({ items: itemIds, pin_token: pinToken, payment_method: 'wallet', wallet_currency: payCurrency });
      toast.success('Payment successful! Your order is now in the Paid tab.');
      onClose();
      navigate('/procurement?status=paid');
    } catch (e) {
      const msg = e.response?.data?.detail || 'Payment failed';
      toast.error(msg, { duration: 7000 });
    } finally { setPaying(false); }
  };

  const payBankTransfer = async () => {
    setPaying(true);
    try {
      const itemIds = await ensureOrder();
      const res = await procurementAPI.checkout({ items: itemIds, payment_method: 'bank_transfer' });
      if (res.data?.authorization_url) { window.location.href = res.data.authorization_url; return; }
      toast.error('Could not start bank transfer. Please try again.');
      setPaying(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Bank transfer failed');
      setPaying(false);
    }
  };

  const sellerBadge = product.seller_type === 'factory'
    ? { icon: Factory, label: 'Factory Verified', color: 'text-emerald-600 bg-emerald-50' }
    : product.seller_type === 'merchant'
    ? { icon: Shield, label: 'Pro Merchant', color: 'text-blue-600 bg-blue-50' }
    : { icon: Store, label: 'Seller', color: 'text-slate-500 bg-slate-50' };

  const SellerIcon = sellerBadge.icon;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl w-[95vw] mx-0 max-h-[92dvh] p-0 overflow-hidden flex flex-col rounded-2xl shadow-2xl" data-testid="sourcing-checkout-modal">
        <DialogTitle className="sr-only">{product?.title || 'Product Detail'}</DialogTitle>
        <DialogDescription className="sr-only">Product details, pricing, and order options</DialogDescription>
        {/* Close button */}
        <button onClick={onClose} className="absolute top-3 right-3 z-20 w-9 h-9 rounded-full bg-white/95 shadow-lg flex items-center justify-center hover:bg-white hover:scale-110 active:scale-95 transition-all duration-200" data-testid="modal-close-btn">
          <X className="h-4 w-4 text-slate-600" />
        </button>

        <div className={`flex-1 overflow-y-auto overscroll-contain scroll-smooth ${step === 'payment' ? 'hidden' : ''}`}>
          {/* ── TOP: IMAGE + INFO SPLIT ── */}
          <div className="md:flex">
            {/* Image gallery with smooth transitions */}
            <div className="md:w-[45%] bg-gradient-to-b from-slate-50 to-white relative flex-shrink-0">
              <div className="aspect-square overflow-hidden relative group">
                {loading && <div className="absolute inset-0 bg-white/70 z-10 flex items-center justify-center backdrop-blur-sm"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>}
                <img key={currentImg} src={currentImg} alt="" className="w-full h-full object-contain animate-[fadeIn_0.3s_ease-out]" referrerPolicy="no-referrer" />
                <span className="absolute top-3 left-3 bg-gradient-to-r from-red-500 to-red-600 text-white text-[10px] font-bold px-2.5 py-1 rounded-full shadow-sm">{product.source?.toUpperCase()}</span>
                {images.length > 1 && (
                  <>
                    <button onClick={() => setActiveImage(Math.max(0, activeImage - 1))} className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-white/90 rounded-full flex items-center justify-center shadow-md opacity-0 group-hover:opacity-100 hover:bg-white hover:scale-110 transition-all duration-200"><ChevronLeft className="h-4 w-4" /></button>
                    <button onClick={() => setActiveImage(Math.min(images.length - 1, activeImage + 1))} className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-white/90 rounded-full flex items-center justify-center shadow-md opacity-0 group-hover:opacity-100 hover:bg-white hover:scale-110 transition-all duration-200"><ChevronRight className="h-4 w-4" /></button>
                  </>
                )}
                {/* Image counter */}
                {images.length > 1 && images.indexOf(currentImg) >= 0 && (
                  <div className="absolute bottom-2 right-2 bg-black/50 text-white text-[10px] px-2 py-0.5 rounded-full backdrop-blur-sm">
                    {images.indexOf(currentImg) + 1} / {images.length}
                  </div>
                )}
              </div>
              {images.length > 1 && (
                <div className="flex gap-1.5 px-3 py-2.5 overflow-x-auto scroll-smooth snap-x">
                  {images.slice(0, 8).map((img, i) => (
                    <button key={i} onClick={() => { setActiveImage(i); setManualPick(true); }}
                      className={`w-14 h-14 rounded-lg overflow-hidden flex-shrink-0 border-2 transition-all duration-200 snap-start ${currentImg === img ? 'border-primary scale-105 shadow-md' : 'border-transparent opacity-50 hover:opacity-80 hover:border-slate-200'}`}>
                      <img src={img} alt="" className="w-full h-full object-cover" referrerPolicy="no-referrer" />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Product info */}
            <div className="md:w-[55%] p-4 md:p-5 space-y-4 overflow-y-auto">
              {/* Title (auto-translated) */}
              <div>
                <h2 className="text-sm md:text-base font-bold text-slate-900 leading-snug" data-testid="product-title">{product.title_en || product.title}</h2>
                {product.title_en && <p className="text-[10px] text-slate-400 mt-0.5 line-clamp-1">{product.title}</p>}
              </div>

              {/* Seller badge + rating */}
              <div className="flex flex-wrap items-center gap-2">
                <span className={`inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full ${sellerBadge.color}`}>
                  <SellerIcon className="h-3 w-3" /> {sellerBadge.label}
                </span>
                {product.seller_name && <span className="text-[10px] text-slate-400">{product.seller_name}</span>}
                {product.level && (
                  <span className="inline-flex items-center gap-0.5 text-[10px] text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded-full">
                    <Star className="h-2.5 w-2.5 fill-amber-500 text-amber-500" /> {product.level}
                  </span>
                )}
                {product.sold > 0 && <span className="text-[10px] text-slate-400">{product.sold > 1000 ? `${(product.sold/1000).toFixed(1)}k` : product.sold} sold</span>}
              </div>

              {/* ── PRICE SECTION ── */}
              <div className="bg-gradient-to-r from-red-50 via-orange-50 to-amber-50 rounded-xl p-4 border border-red-100/50 shadow-sm">
                <div className="flex items-end gap-2">
                  <span className="text-2xl font-black text-red-600">¥{unitPrice?.toFixed(2)}</span>
                  {baseUnit !== unitPrice && <span className="text-xs text-slate-400 line-through mb-0.5">¥{baseUnit?.toFixed(2)}</span>}
                  {ngnUnit && <span className="text-xs text-slate-500 mb-0.5">≈ ₦{ngnUnit.toLocaleString()}</span>}
                </div>
                <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mt-1">
                  {markup > 0 && <p className="text-[9px] text-slate-400">Includes {markup}% service markup</p>}
                  <span className="inline-flex items-center gap-1 text-[10px] font-bold text-slate-600 bg-white/70 border border-slate-200 px-2 py-0.5 rounded-full" data-testid="moq-badge">
                    <Package className="h-3 w-3 text-slate-400" /> MOQ: {product.moq || 1} pc{(product.moq || 1) > 1 ? 's' : ''}
                  </span>
                  <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full border bg-white/70 border-slate-200" data-testid="freight-badge">
                    <Truck className={`h-3 w-3 ${freight?.status === 'free' ? 'text-emerald-500' : 'text-slate-400'}`} />
                    {freight?.status === 'free' ? (
                      <span className="text-emerald-600">FREE China delivery</span>
                    ) : freight?.status === 'flat' && freight.fee_rmb != null ? (
                      <span className="text-slate-600">China delivery ¥{Number(freight.fee_rmb).toFixed(2)}</span>
                    ) : freight?.status === 'quoted' && freight.fee_rmb != null ? (
                      <span className="text-slate-600">China delivery from ¥{Number(freight.fee_rmb).toFixed(2)}</span>
                    ) : (
                      <span className="text-slate-400">Delivery: set by supplier at purchase</span>
                    )}
                  </span>
                </div>

                {/* Wholesale price tiers */}
                {product.price_range?.length > 1 && (
                  <div className="mt-2 flex gap-1 flex-wrap">
                    {product.price_range.map((pr, i) => (
                      <div key={i} className={`text-center px-2 py-1 rounded text-[10px] border ${quantity >= pr.moq ? 'bg-red-100 border-red-200 text-red-700 font-bold' : 'bg-white border-slate-200 text-slate-500'}`}>
                        <div className="font-bold">¥{pr.marked_price?.toFixed(2)}</div>
                        <div>≥ {pr.moq} pcs</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* ── VARIANTS ── */}
              {product.variant_groups && Object.keys(product.variant_groups).length > 0 && (
                Object.entries(product.variant_groups).map(([groupName, options]) => (
                  <div key={groupName}>
                    <p className="text-xs font-semibold text-slate-600 mb-1.5">{trv(groupName) !== groupName ? trv(groupName) : translateLabel(groupName)} <span className="text-slate-400 font-normal">{selectedVariants[groupName] ? `— ${trv(selectedVariants[groupName])}` : ''}</span></p>
                    <div className="flex flex-wrap gap-1.5">
                      {options.map((val, i) => {
                        // Find SKU image for this option
                        const skuForOpt = product.skus?.find(s => s.options?.[groupName] === val && s.img_url);
                        return (
                          <button key={i} onClick={() => {
                            setSelectedVariants(prev => ({...prev, [groupName]: val}));
                            setManualPick(false);
                            if (skuForOpt?.img_url) {
                              const idx = images.indexOf(skuForOpt.img_url);
                              if (idx >= 0) setActiveImage(idx);
                            }
                          }}
                            data-testid={`variant-${groupName}-${i}`}
                            className={`flex items-center gap-1.5 px-2.5 py-1.5 text-[11px] rounded-xl border-2 transition-all duration-200 active:scale-95 ${selectedVariants[groupName] === val ? 'border-primary bg-primary/5 text-primary font-bold shadow-md shadow-primary/10' : 'border-slate-200 text-slate-600 hover:border-primary/40 hover:bg-slate-50 hover:shadow-sm'}`}>
                            {skuForOpt?.img_url && <img src={skuForOpt.img_url} alt="" className="w-6 h-6 rounded object-cover" referrerPolicy="no-referrer" />}
                            <span className="max-w-[120px] truncate">{trv(val)}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))
              )}

              {/* ── QUANTITY ── */}
              <div className="flex items-center gap-3">
                <span className="text-xs font-semibold text-slate-600">Quantity</span>
                <div className="flex items-center border-2 border-slate-200 rounded-xl overflow-hidden shadow-sm">
                  <button onClick={() => setQuantity(Math.max(product.moq || 1, quantity - 1))} className="px-3.5 py-2 hover:bg-slate-50 active:bg-slate-100 text-base touch-manipulation" data-testid="qty-minus"><Minus className="h-4 w-4" /></button>
                  <input type="number" value={quantity} onChange={e => { const v = parseInt(e.target.value) || 1; setQuantity(Math.max(product.moq || 1, v)); }}
                    className="w-12 text-center text-sm font-bold border-x border-slate-200 py-1 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" data-testid="qty-display" />
                  <button onClick={() => setQuantity(quantity + 1)} className="px-3.5 py-2 hover:bg-slate-50 active:bg-slate-100 text-base touch-manipulation" data-testid="qty-plus"><Plus className="h-4 w-4" /></button>
                </div>
                {product.moq > 1 && <span className="text-[10px] text-amber-600 bg-amber-50 px-2 py-0.5 rounded">MOQ: {product.moq}</span>}
                {skuStock > 0 && <span className="text-[10px] text-slate-400">{skuStock.toLocaleString()} in stock</span>}
                {variantGroups.length > 0 && (
                  <button onClick={addLine} className="ml-auto flex items-center gap-1 px-2.5 py-1.5 text-[11px] font-bold text-primary border-2 border-primary/30 rounded-xl hover:bg-primary/5 active:scale-95 transition-all" data-testid="add-selection-btn">
                    <Plus className="h-3 w-3" /> Add selection
                  </button>
                )}
              </div>

              {/* ── MULTI-SKU LINES ── */}
              {lines.length > 0 && (
                <div className="border-2 border-primary/15 bg-primary/[0.03] rounded-xl p-3 space-y-2" data-testid="sku-lines-panel">
                  <p className="text-[11px] font-bold text-slate-600">Your selections ({lines.length})</p>
                  {lines.map(l => (
                    <div key={l.id} className="flex items-center gap-2 bg-white rounded-lg border border-slate-100 p-2" data-testid={`sku-line-${l.id}`}>
                      {l.image && <img src={l.image} alt="" className="w-9 h-9 rounded object-cover flex-shrink-0" referrerPolicy="no-referrer" />}
                      <div className="flex-1 min-w-0">
                        <p className="text-[11px] font-semibold text-slate-700 truncate">{l.variantStr || 'Standard'}</p>
                        <p className="text-[10px] text-slate-400">¥{(l.baseUnit || 0).toFixed(2)}/unit</p>
                      </div>
                      <div className="flex items-center border border-slate-200 rounded-lg overflow-hidden">
                        <button onClick={() => updateLineQty(l.id, -1)} className="px-1.5 py-0.5 hover:bg-slate-50" data-testid={`line-minus-${l.id}`}><Minus className="h-3 w-3" /></button>
                        <span className="px-2 text-xs font-bold" data-testid={`line-qty-${l.id}`}>{l.qty}</span>
                        <button onClick={() => updateLineQty(l.id, 1)} className="px-1.5 py-0.5 hover:bg-slate-50" data-testid={`line-plus-${l.id}`}><Plus className="h-3 w-3" /></button>
                      </div>
                      <span className="text-xs font-bold text-slate-700 w-16 text-right">¥{((l.baseUnit || 0) * l.qty).toFixed(2)}</span>
                      <button onClick={() => removeLine(l.id)} className="text-red-400 hover:text-red-600 p-1" data-testid={`line-remove-${l.id}`}><X className="h-3.5 w-3.5" /></button>
                    </div>
                  ))}
                </div>
              )}

              {/* Notes */}
              <div>
                <span className="text-xs font-semibold text-slate-600">Notes (optional)</span>
                <Input value={notes} onChange={e => setNotes(e.target.value)} placeholder="e.g. specific color, size preference" className="mt-1.5 h-10 text-sm rounded-xl" data-testid="sourcing-notes-input" />
              </div>
            </div>
          </div>

          {/* ── TABS: Detail / Attributes / Reviews ── */}
          {(product.attributes?.length > 0 || product.review || product.description_html) && (
            <div className="border-t">
              <div className="flex border-b bg-slate-50">
                {['detail', ...(product.attributes?.length ? ['specs'] : []), ...(product.review ? ['seller'] : [])].map(tab => (
                  <button key={tab} onClick={() => setActiveTab(tab)}
                    className={`px-4 py-2.5 text-xs font-semibold capitalize transition ${activeTab === tab ? 'text-primary border-b-2 border-primary bg-white' : 'text-slate-500 hover:text-slate-700'}`}>
                    {tab === 'specs' ? 'Specifications' : tab === 'seller' ? 'Seller Info' : 'Product Detail'}
                  </button>
                ))}
              </div>
              <div className="p-4 max-h-[250px] overflow-y-auto">
                {activeTab === 'detail' && product.description_html && (
                  <div className="prose prose-sm max-w-none [&_img]:max-w-full [&_img]:rounded-lg [&_img]:referrerpolicy-no-referrer" dangerouslySetInnerHTML={{ __html: product.description_html }} />
                )}
                {activeTab === 'detail' && !product.description_html && (
                  <p className="text-xs text-slate-400">No detailed description available.</p>
                )}
                {activeTab === 'specs' && product.attributes?.length > 0 && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1">
                    {product.attributes.map((attr, i) => (
                      <div key={i} className="flex py-1.5 border-b border-slate-100 text-xs">
                        <span className="text-slate-400 w-28 flex-shrink-0">{translateLabel(attr.name)}</span>
                        <span className="text-slate-700 font-medium">{translateLabel(attr.value)}</span>
                      </div>
                    ))}
                  </div>
                )}
                {activeTab === 'seller' && product.review && (
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                        <SellerIcon className="h-6 w-6 text-primary" />
                      </div>
                      <div>
                        <p className="text-sm font-bold text-slate-800">{product.seller_name || 'Seller'}</p>
                        <p className="text-[10px] text-slate-400 capitalize">{product.seller_type} · {product.category_path || product.category}</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                      {[
                        { label: 'Overall', value: product.review.total_score, max: 5 },
                        { label: 'Service', value: product.review.service_score, max: 5 },
                        { label: 'Logistics', value: product.review.logistics_score, max: 5 },
                        { label: 'After-sale', value: product.review.aftersale_score, max: 5 },
                        { label: 'Return Rate', value: product.review.retention_rate ? `${(product.review.retention_rate * 100).toFixed(0)}%` : null },
                      ].filter(s => s.value != null).map((s, i) => (
                        <div key={i} className="bg-slate-50 rounded-lg p-2 text-center">
                          <p className="text-lg font-black text-primary">{typeof s.value === 'number' ? s.value.toFixed(1) : s.value}</p>
                          <p className="text-[9px] text-slate-400">{s.label}</p>
                        </div>
                      ))}
                    </div>
                    {/* Extra info badges */}
                    {product.extra_info && (
                      <div className="flex flex-wrap gap-1.5">
                        {product.extra_info.isOnePsale && <span className="text-[9px] px-2 py-0.5 bg-green-50 text-green-700 rounded-full">Supports Dropshipping</span>}
                        {product.extra_info.noReason7DReturn && <span className="text-[9px] px-2 py-0.5 bg-blue-50 text-blue-700 rounded-full">7-Day Return</span>}
                        {product.extra_info.isOnePsaleFreePostage && <span className="text-[9px] px-2 py-0.5 bg-emerald-50 text-emerald-700 rounded-full">Free Shipping (Dropship)</span>}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* ── PAYMENT STEP ── */}
        {step === 'payment' && (
          <div className="flex-1 overflow-y-auto p-4 md:p-6 space-y-4" data-testid="sourcing-payment-step">
            <button onClick={() => setStep('product')} className="flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-primary transition-colors" data-testid="payment-back-btn">
              <ArrowLeft className="h-3.5 w-3.5" /> Back to product
            </button>
            {/* Order summary */}
            <div className="flex gap-3 bg-slate-50 rounded-xl p-3 border border-slate-100">
              {currentImg && <img src={currentImg} alt="" className="w-16 h-16 rounded-lg object-cover flex-shrink-0" referrerPolicy="no-referrer" />}
              <div className="min-w-0 flex-1">
                <p className="text-xs font-bold text-slate-800 line-clamp-2">{product.title}</p>
                {lines.length > 0 ? (
                  <div className="mt-1 space-y-0.5">
                    {lines.map(l => (
                      <p key={l.id} className="text-[10px] text-slate-500 truncate">{l.qty} × {l.variantStr || 'Standard'} @ ¥{(l.baseUnit || 0).toFixed(2)}</p>
                    ))}
                  </div>
                ) : (
                  <>
                    {variantStr && <p className="text-[10px] text-slate-500 mt-0.5 truncate">{variantStr}</p>}
                    <p className="text-[10px] text-slate-500 mt-0.5">{quantity} × ¥{(baseUnit || 0).toFixed(2)}</p>
                  </>
                )}
              </div>
            </div>
            {/* Price breakdown */}
            <div className="bg-white border-2 border-slate-100 rounded-xl p-4 space-y-2" data-testid="price-breakdown">
              <div className="flex justify-between text-xs text-slate-600"><span>Product cost ({totalQty} pcs)</span><span className="font-semibold">¥{baseTotal.toFixed(2)}</span></div>
              <div className="flex justify-between text-xs text-slate-600"><span>Service fee</span><span className="font-semibold">¥{feeTotal.toFixed(2)}</span></div>
              <div className="flex justify-between text-xs" data-testid="domestic-freight-line">
                <span className="text-slate-600">China domestic delivery</span>
                {freight?.status === 'free' ? (
                  <span className="font-semibold text-emerald-600">FREE</span>
                ) : freight?.status === 'flat' && freight.fee_rmb != null ? (
                  <span className="font-semibold text-slate-700">¥{Number(freight.fee_rmb).toFixed(2)}</span>
                ) : freight?.status === 'quoted' && freight.fee_rmb != null ? (
                  <span className="font-semibold text-slate-700">from ¥{Number(freight.fee_rmb).toFixed(2)} <span className="text-[9px] font-normal text-slate-400">(final at purchase)</span></span>
                ) : (
                  <span className="text-[10px] text-slate-400">Unconfirmed — billed only if supplier charges</span>
                )}
              </div>
              <div className="border-t border-dashed pt-2 flex justify-between items-baseline">
                <span className="text-sm font-bold text-slate-800">Total</span>
                <div className="text-right">
                  <span className="text-xl font-black text-red-600" data-testid="payment-total-rmb">¥{totalPrice.toFixed(2)}</span>
                  {ngnTotal && <p className="text-[10px] text-slate-500">≈ ₦{ngnTotal.toLocaleString()} @ ₦{exchangeRate}/¥</p>}
                </div>
              </div>
            </div>
            {/* Payment methods */}
            <p className="text-xs font-bold text-slate-700">Choose payment method</p>
            <div className="space-y-2">
              <button onClick={() => payWithWallet('rmb')} disabled={paying}
                className="w-full flex items-center justify-between p-3.5 rounded-xl border-2 border-slate-200 hover:border-primary hover:bg-primary/5 transition-all active:scale-[0.99] disabled:opacity-50"
                data-testid="pay-rmb-wallet-btn">
                <span className="flex items-center gap-2.5 text-sm font-semibold text-slate-700"><Wallet className="h-4.5 w-4.5 text-red-500" /> RMB Wallet</span>
                <span className={`text-xs font-bold ${(wallet?.rmb_balance || 0) >= totalPrice ? 'text-emerald-600' : 'text-slate-400'}`}>¥{(wallet?.rmb_balance || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}</span>
              </button>
              <button onClick={() => payWithWallet('ngn')} disabled={paying}
                className="w-full flex items-center justify-between p-3.5 rounded-xl border-2 border-slate-200 hover:border-primary hover:bg-primary/5 transition-all active:scale-[0.99] disabled:opacity-50"
                data-testid="pay-ngn-wallet-btn">
                <span className="flex items-center gap-2.5 text-sm font-semibold text-slate-700"><Wallet className="h-4.5 w-4.5 text-emerald-600" /> NGN Wallet</span>
                <span className={`text-xs font-bold ${(wallet?.ngn_balance || 0) >= (ngnTotal || 0) ? 'text-emerald-600' : 'text-slate-400'}`}>₦{(wallet?.ngn_balance || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}</span>
              </button>
              <button onClick={payBankTransfer} disabled={paying}
                className="w-full flex items-center justify-between p-3.5 rounded-xl border-2 border-slate-200 hover:border-primary hover:bg-primary/5 transition-all active:scale-[0.99] disabled:opacity-50"
                data-testid="pay-bank-transfer-btn">
                <span className="flex items-center gap-2.5 text-sm font-semibold text-slate-700"><Landmark className="h-4.5 w-4.5 text-blue-600" /> Bank Transfer / Card</span>
                <span className="text-[10px] text-slate-400">via Paystack</span>
              </button>
            </div>
            {paying && <div className="flex items-center justify-center gap-2 text-xs text-slate-500 py-2"><Loader2 className="h-4 w-4 animate-spin" /> Processing your order…</div>}
            <p className="text-[10px] text-slate-400 flex items-center gap-1"><CheckCircle2 className="h-3 w-3 text-emerald-500" /> After payment your order appears in the Paid tab and our team purchases it immediately.</p>
          </div>
        )}

        {/* ── STICKY ORDER BAR ── */}
        <div className={`sticky bottom-0 bg-white/95 backdrop-blur-sm border-t shadow-[0_-4px_20px_rgba(0,0,0,0.08)] px-3 sm:px-4 py-3 flex items-center gap-2 sm:gap-3 z-10 ${step === 'payment' ? 'hidden' : ''}`} data-testid="order-bar">
          <div className="flex-1 min-w-0">
            <div className="flex items-baseline gap-2">
              <span key={totalPrice} className="text-lg font-black text-red-600 transition-all">¥{totalPrice.toFixed(2)}</span>
              {ngnTotal && <span className="text-xs text-slate-500 bg-slate-50 px-1.5 py-0.5 rounded hidden xs:inline">≈ ₦{ngnTotal.toLocaleString()}</span>}
            </div>
            <p className="text-[9px] text-slate-400 truncate">{lines.length > 0 ? `${lines.length} selection(s), ${totalQty} pcs + fee` : `${quantity} × ¥${(baseUnit || 0).toFixed(2)} + fee${Object.values(selectedVariants).length > 0 ? ` · ${Object.values(selectedVariants).join(', ')}` : ''}`}</p>
          </div>
          <Button onClick={addToOrders} disabled={paying} variant="outline" className="h-11 px-3 sm:px-4 font-bold text-xs sm:text-sm flex-shrink-0 rounded-xl border-2 active:scale-95 transition-all" data-testid="sourcing-add-order-btn">
            <ShoppingCart className="h-4 w-4 sm:mr-1.5" /><span className="hidden sm:inline">Add to Orders</span>
          </Button>
          <Button onClick={startCheckout} disabled={paying} className="h-11 px-4 sm:px-6 bg-gradient-to-r from-primary to-primary/90 hover:from-primary/90 hover:to-primary font-bold text-xs sm:text-sm flex-shrink-0 rounded-xl shadow-lg shadow-primary/20 active:scale-95 transition-all" data-testid="sourcing-order-btn">
            <ShoppingBag className="h-4 w-4 mr-1.5" /> Buy Now
          </Button>
        </div>

        {/* PIN dialogs for wallet payment */}
        <PINSetupDialog open={showPINSetup} onOpenChange={setShowPINSetup} onSuccess={() => { setShowPINSetup(false); setShowPINVerify(true); }} />
        <PINVerificationDialog open={showPINVerify} onOpenChange={setShowPINVerify} onVerified={completeWalletPayment} title="Enter PIN to Pay" />
      </DialogContent>
    </Dialog>
  );
}

// ─── MAIN HERO SOURCING SEARCH ──────────────────────────────────────────────
export default function HeroSourcingSearch({ embedded = false }) {
  const { user } = useAuth();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showCheckout, setShowCheckout] = useState(false);
  const [exchangeRate, setExchangeRate] = useState(216);
  const [markup, setMarkup] = useState(5);
  const [minFee, setMinFee] = useState(2);
  const [apiMessage, setApiMessage] = useState('');
  const [isDemo, setIsDemo] = useState(false);
  const [showMembershipGate, setShowMembershipGate] = useState(false);
  const fileInputRef = useRef(null);

  const fetchRate = async () => {
    try { const r = await exchangeRateAPI.getRate(); setExchangeRate(r.data?.ngn_to_rmb || 216); } catch {}
    try { const c = await sourcingAPI.getConfig(); if (c.data?.markup_min_fee_rmb != null) setMinFee(c.data.markup_min_fee_rmb); } catch {}
  };

  const handleSearch = async (e) => {
    e?.preventDefault();
    const trimmed = query.trim();
    if (!trimmed) return;
    // Extract first URL from pasted share text (e.g. "看看这个 https://qr.1688.com/s/xxx 好东西")
    const urlMatch = trimmed.match(/https?:\/\/[^\s\u4e00-\u9fa5]+/i);
    const candidate = urlMatch ? urlMatch[0].replace(/[),.;!"'”】)]+$/, '') : trimmed;
    const isLink = !!urlMatch && ['1688.com','taobao.com','tmall.com','pinduoduo.com','yangkeduo.com','tb.cn','pdd.net','qr.1688.com','s.click.taobao.com'].some(d => candidate.includes(d));
    setSearching(true); setHasSearched(true); setApiMessage(''); setIsDemo(false);
    try {
      await fetchRate();
      if (isLink) {
        const res = await sourcingAPI.parseLink(candidate);
        if (res.data?.product) {
          setMarkup(res.data.markup_percent || 5);
          if (res.data.markup_min_fee_rmb != null) setMinFee(res.data.markup_min_fee_rmb);
          setIsDemo(!!res.data.is_demo);
          setSelectedProduct(res.data.product);
          setShowCheckout(true);
          setResults([]);
          // Translation may finish in background — re-pull from cache for English text
          const p0 = res.data.product;
          if (!p0.translations || Object.keys(p0.translations).length === 0) {
            const repoll = async () => {
              try {
                const r2 = await sourcingAPI.parseLink(candidate);
                const p2 = r2.data?.product;
                if (p2?.translations && Object.keys(p2.translations).length > 0) {
                  setSelectedProduct(prev => (prev && prev.id === p2.id ? p2 : prev));
                  return true;
                }
              } catch {}
              return false;
            };
            setTimeout(async () => { if (!(await repoll())) setTimeout(repoll, 14000); }, 9000);
          }
        } else if (user) {
          // Auto-fallback: open the manual order form prefilled with the link — no error prompt
          const params = new URLSearchParams({ prefill: 'add', product_link: candidate });
          window.location.href = `/procurement?${params.toString()}`;
          return;
        } else {
          setApiMessage(res.data?.message || 'Could not load product from link');
          setResults([]);
        }
      } else {
        const res = await sourcingAPI.searchText(trimmed);
        setResults(res.data?.products || []);
        setMarkup(res.data?.markup_percent || 5);
        setIsDemo(!!res.data?.is_demo);
        if (res.data?.message) setApiMessage(res.data.message);
      }
    } catch {
      if (isLink && user) {
        const params = new URLSearchParams({ prefill: 'add', product_link: candidate });
        window.location.href = `/procurement?${params.toString()}`;
        return;
      }
      toast.error('Search failed. Please try again.'); setResults([]);
    }
    finally { setSearching(false); }
  };

  const handleSearchWithQuery = async (keyword) => {
    if (!keyword.trim()) return;
    setSearching(true); setHasSearched(true); setApiMessage(''); setIsDemo(false);
    try {
      await fetchRate();
      const res = await sourcingAPI.searchText(keyword);
      setResults(res.data?.products || []);
      setMarkup(res.data?.markup_percent || 5);
      setIsDemo(!!res.data?.is_demo);
      if (res.data?.message) setApiMessage(res.data.message);
    } catch { toast.error('Search failed'); }
    finally { setSearching(false); }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error('Image must be under 5MB'); return; }
    toast.info('Searching by image...', { duration: 2000 });
    const reader = new FileReader();
    reader.onloadend = async () => {
      setSearching(true); setHasSearched(true); setApiMessage(''); setIsDemo(false);
      try {
        await fetchRate();
        const res = await sourcingAPI.searchImage(reader.result);
        const products = res.data?.products || [];
        setResults(products);
        setMarkup(res.data?.markup_percent || 5);
        setIsDemo(!!res.data?.is_demo);
        if (res.data?.message) setApiMessage(res.data.message);
        else if (products.length === 0) setApiMessage('No matching products found for this image. Try a clearer product photo.');
      } catch (err) {
        toast.error('Image search failed. Please try a different image.');
        setApiMessage('Image search failed. Try uploading a clear product photo from a marketplace.');
      }
      finally { setSearching(false); }
    };
    reader.readAsDataURL(file);
    // Reset the input so the same file can be selected again
    e.target.value = '';
  };

  const handleSelectProduct = (product) => {
    setSelectedProduct(product);
    setShowCheckout(true);
  };

  return (
    <div data-testid="hero-sourcing-search">
      {/* Compact Search Bar */}
      <div className="bg-gradient-to-r from-slate-900 via-slate-800 to-primary/90 rounded-2xl px-3 py-3 md:px-4 md:py-3.5 shadow-lg relative overflow-hidden">
        <div className="relative z-10">
          <form onSubmit={handleSearch} className="flex gap-2 items-center">
            <div className="relative flex-1 min-w-0">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-primary" />
              <input value={query} onChange={(e) => setQuery(e.target.value)}
                placeholder={typeof window !== 'undefined' && window.innerWidth < 640 ? "Search or paste product link…" : "Search or paste a 1688 / Taobao / Pinduoduo product link…"}
                className="w-full h-11 md:h-12 pl-10 pr-12 bg-white rounded-xl text-sm text-slate-800 placeholder:font-bold placeholder:text-slate-400 border-2 border-white/20 focus:border-primary/60 focus:ring-2 focus:ring-primary/30 outline-none shadow-md transition-colors truncate"
                data-testid="sourcing-search-input" />
              <button type="button" onClick={() => fileInputRef.current?.click()}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-lg bg-slate-50 hover:bg-primary/10 transition-colors text-slate-500 hover:text-primary border border-slate-200"
                title="Search by image" data-testid="sourcing-image-upload-btn">
                <Camera className="h-4 w-4" />
              </button>
              <input ref={fileInputRef} type="file" accept="image/*" onChange={handleImageUpload} className="hidden" />
            </div>
            <Button type="submit" disabled={searching || !query.trim()} className="h-11 md:h-12 px-4 md:px-6 bg-primary hover:bg-primary/90 rounded-xl font-bold shadow-md flex-shrink-0" data-testid="sourcing-search-btn">
              {searching ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Search className="h-4 w-4 sm:hidden" /><span className="hidden sm:inline">Search</span></>}
            </Button>
          </form>
          <div className="flex gap-1.5 mt-2 overflow-x-auto scrollbar-none [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            <span className="flex items-center gap-1 text-white/60 text-[10px] font-bold flex-shrink-0 pr-1"><ShoppingBag className="h-3 w-3" /> Source from China:</span>
            {['Electronics','Fashion','Phone Cases','Bags','Shoes','Home Decor'].map(tag => (
              <button key={tag} onClick={() => { setQuery(tag); handleSearchWithQuery(tag); }}
                className="px-2.5 py-0.5 bg-white/10 text-white/70 rounded-full text-[10px] font-medium hover:bg-white/20 hover:text-white transition-colors flex-shrink-0 whitespace-nowrap"
                data-testid={`sourcing-tag-${tag.toLowerCase().replace(' ','-')}`}>{tag}</button>
            ))}
          </div>
        </div>
      </div>

      {/* Results */}
      {hasSearched && (
        <div className="mt-6">
          {searching ? (
            <div className="flex flex-col items-center py-12">
              <Loader2 className="h-10 w-10 animate-spin text-primary mb-3" />
              <p className="text-slate-500">Searching Chinese platforms...</p>
            </div>
          ) : results.length > 0 ? (
            <>
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-bold text-slate-700">{results.length} products found</p>
                  {isDemo && <span className="text-[10px] bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-medium">Demo</span>}
                </div>
                <button onClick={() => { setResults([]); setHasSearched(false); setQuery(''); }} className="text-xs text-slate-500 hover:text-primary flex items-center gap-1"><X className="h-3 w-3" /> Clear</button>
              </div>
              {isDemo && apiMessage && (
                <div className="mb-4 bg-amber-50 border border-amber-200 rounded-lg px-4 py-2.5 text-xs text-amber-700 flex items-center gap-2">
                  <Info className="h-3.5 w-3.5 flex-shrink-0" />{apiMessage}
                </div>
              )}
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                {results.map(product => (
                  <SourcingProductCard key={product.id} product={product} onSelect={handleSelectProduct} exchangeRate={exchangeRate} />
                ))}
              </div>
            </>
          ) : apiMessage ? (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 text-center">
              <ShoppingBag className="h-10 w-10 text-amber-400 mx-auto mb-2" />
              <p className="text-sm text-amber-800 font-medium">{apiMessage}</p>
              <button
                data-testid="manual-order-fallback-btn"
                onClick={() => {
                  const m = (query || '').match(/https?:\/\/[^\s\u4e00-\u9fa5]+/i);
                  const link = m ? m[0].replace(/[),.;!"'”】)]+$/, '') : (query || '');
                  const params = new URLSearchParams({ prefill: 'add', product_link: link });
                  window.location.href = `/procurement?${params.toString()}`;
                }}
                className="mt-3 inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-amber-600 text-white text-xs font-semibold hover:bg-amber-700 transition-colors"
              >
                Place this order manually instead
              </button>
            </div>
          ) : null}
        </div>
      )}

      {/* Product Detail Modal */}
      <SourcingCheckoutModal
        product={selectedProduct} open={showCheckout}
        onClose={() => setShowCheckout(false)}
        exchangeRate={exchangeRate} markup={markup} minFee={minFee}
        isAuthenticated={!!user} isDemo={isDemo}
        isMember={user?.is_member}
        onMembershipRequired={() => setShowMembershipGate(true)}
      />

      {/* Membership Gate */}
      <MembershipGate
        open={showMembershipGate}
        onClose={() => setShowMembershipGate(false)}
        action="place sourcing orders"
      />
    </div>
  );
}
